import _ from "lodash";
import { useEffect } from "react";
import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Text } from "@pentabd/ui";

import { InputDateControllerRow, InputTextRow, PayScaleRow } from "@components";
import { SelectController } from "@components/SelectController";
import { SelectControllerRow } from "@components/SelectControllerRow";

import { useBaseData } from "@hooks/useBaseData";

import {
  getConvertedPositionDataWithPostType,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";

import { PositionType } from "@type/HRM/position";

import { BASE_DATA } from "@constants/base-data";

import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import {
  getFilteredGradeData,
  getFilteredUnitPositionData,
} from "@helpers/convertedGradeAndUnitPositionData";
import { getPositionColor } from "@helpers/getPositionColor";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

import {
  InitiatePromotionFormValidationDataType,
  RECOMMENDED_POSITION,
} from "@validations/HRM/promotion/initiatePromotionFormValidation";

const { WINGS, POSITIONS, UNIT_TREE, POST_TYPE, OFFICE, GRADES } = BASE_DATA;

export const RecommendedPosition = ({
  isDisabled,
}: {
  isDisabled: boolean;
}) => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const { data: positions } = useBaseData(POSITIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: unitTree } = useBaseData(UNIT_TREE);
  const { data: postType } = useMasterData(POST_TYPE);
  const { data: offices } = useBaseData(OFFICE);
  const { data: grades } = useBaseData(GRADES);

  const {
    register,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useFormContext<InitiatePromotionFormValidationDataType>();

  let wing = watch(RECOMMENDED_POSITION.DEPARTMENT) as any;
  let post = watch(RECOMMENDED_POSITION.POST) as any;
  const grade = watch(RECOMMENDED_POSITION.GRADE) as number;

  useEffect(() => {
    const physicalOfficeId = wings?.find(
      (item: any) => item.id === wing,
    )?.physicalOffice;

    const office = offices?.find(
      (item: any) => item.id === Number(physicalOfficeId),
    );

    const districtName = language === "en" ? "nameEn" : "nameBn";

    setValue(RECOMMENDED_POSITION.NAME_OF_OFFICE, office?.name);
    setValue(RECOMMENDED_POSITION.DISTRICT, office?.district[districtName]);
  }, [wing, language, setValue, wings, offices]);

  useEffect(() => {
    if (grade) {
      setValue(
        RECOMMENDED_POSITION.PAY_SCALE_MIN,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleFrom,
      );
      setValue(
        RECOMMENDED_POSITION.PAY_SCALE_MAX,
        {
          ..._.find(grades, (_grade) => {
            return _grade.id === grade;
          }),
        }.salaryScaleTo,
      );
    }
  }, [grade, grades, setValue]);

  return (
    <div>
      <div className="border-bottom pb-8">
        <Text weight="semibold" color="title" size="md">
          {t("INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.LABEL")}
        </Text>
      </div>
      <div className="d-flex flex-column gap-8 border-bottom py-10 mb-8">
        <InputTextRow
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.POSTING"
          register={{ ...register(RECOMMENDED_POSITION.POSTING) }}
          disabled={isDisabled}
        />
        <div className="row">
          <div className="col-3">
            <Text weight="semibold" size="sm" color="title">
              {t(
                "INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.DEPARTMENT.LABEL",
              )}
            </Text>
            <span className="text-danger">&#42;</span>
          </div>
          <div className="col-9 col-lg-7 d-flex gap-4">
            <Controller
              control={control}
              name={RECOMMENDED_POSITION.DEPARTMENT}
              render={() => {
                return (
                  <DropdownTreeSelect
                    data={unitTreeDataConvert(
                      unitTree[0]?.children || [],
                      language,
                    )}
                    onChange={(e) =>
                      setValue(RECOMMENDED_POSITION.DEPARTMENT, e.id as number)
                    }
                    mode="radioSelect"
                    texts={{
                      placeholder: t(
                        "INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.DEPARTMENT.PLACEHOLDER",
                      ),
                    }}
                    disabled={isDisabled}
                  />
                );
              }}
            />
            {wing && (
              <SelectController
                control={control}
                name={RECOMMENDED_POSITION.DEPARTMENT}
                register={{ ...register(RECOMMENDED_POSITION.DEPARTMENT) }}
                options={getConvertedUnitData(wings, language)}
                setValue={setValue}
                disabled
              />
            )}
          </div>
        </div>

        {wing && (
          <SelectControllerRow
            label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.POST"
            name={RECOMMENDED_POSITION.POST}
            register={{ ...register(RECOMMENDED_POSITION.POST) }}
            options={getConvertedPositionDataWithPostType(
              positions
                .filter(
                  (position: PositionType) => position.unit.id === Number(wing),
                )
                .map((position: PositionType) => ({
                  position: position.position,
                  postType: position.postType,
                })),
              language,
            ).map((position: any) => ({
              ...position,
              customComponent: (
                <Text
                  color={getPositionColor(postType as any, position as any)}
                >
                  {position.label}
                </Text>
              ),
            }))}
            setValue={setValue}
            control={control}
            error={errors as any}
            disabled={isDisabled}
            required
          />
        )}

        {wing && post && (
          <>
            <SelectControllerRow
              label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.GRADE"
              name={RECOMMENDED_POSITION.GRADE}
              register={{ ...register(RECOMMENDED_POSITION.GRADE) }}
              options={getFilteredGradeData(
                positions,
                wing,
                post,
                language,
                isDisabled ? "viewOnly" : "filledPosition", // don't show the empty position info
              )}
              setValue={setValue}
              control={control}
              error={errors as any}
              disabled={isDisabled}
              required
            />

            <SelectControllerRow
              label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.POSITION"
              name={RECOMMENDED_POSITION.POSITION}
              register={{ ...register(RECOMMENDED_POSITION.POSITION) }}
              options={getFilteredUnitPositionData(
                positions,
                wing,
                post,
                language,
                isDisabled ? "viewOnly" : "filledPosition", // don't show the empty position info
              )}
              setValue={setValue}
              control={control}
              error={errors as any}
              disabled={isDisabled}
              required
            />
            <PayScaleRow
              register={register}
              payScaleMin={RECOMMENDED_POSITION.PAY_SCALE_MIN}
              payScaleMax={RECOMMENDED_POSITION.PAY_SCALE_MAX}
            />
            <InputTextRow
              register={{ ...register(RECOMMENDED_POSITION.BASIC_SALARY) }}
              label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.BASIC_SALARY"
              error={errors as any}
              disabled={isDisabled}
            />
          </>
        )}

        {wing && (
          <>
            <InputTextRow
              label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.NAME_OF_THE_OFFICE"
              register={{ ...register(RECOMMENDED_POSITION.NAME_OF_OFFICE) }}
              disabled
            />

            <InputTextRow
              label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.DIST_LOCATION"
              register={{ ...register(RECOMMENDED_POSITION.DISTRICT) }}
              disabled
            />
          </>
        )}

        <InputDateControllerRow
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.JOINING_DATE"
          control={control}
          register={{ ...register(RECOMMENDED_POSITION.JOINING_DATE) }}
          disabled={isDisabled}
          errors={errors as any}
          required
          name={RECOMMENDED_POSITION.JOINING_DATE}
        />

        <InputDateControllerRow
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.TO_DATE"
          control={control}
          register={{ ...register(RECOMMENDED_POSITION.TO_DATE) }}
          name={RECOMMENDED_POSITION.TO_DATE}
          disabled={isDisabled}
        />

        <InputDateControllerRow
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.GO_DATE"
          control={control}
          register={{ ...register(RECOMMENDED_POSITION.GO_DATE) }}
          name={RECOMMENDED_POSITION.GO_DATE}
          disabled={isDisabled}
        />

        <InputTextRow
          register={{ ...register(RECOMMENDED_POSITION.GO_NO) }}
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.GO_NO"
          disabled={isDisabled}
        />

        <InputTextRow
          register={{ ...register(RECOMMENDED_POSITION.NATURE_OF_PROMOTION) }}
          label="INITIATE_PROMOTION.FORM.POSITION.RECOMMENDED.NATURE_OF_PROMOTION"
          disabled={isDisabled}
        />
      </div>
    </div>
  );
};
